import { type Objectish, type Patch, applyPatches } from "immer";
import * as im from "immutable";
import { v4 as uuidV4 } from "uuid";
import {
	type PromiseActorLogic,
	assign,
	type createActor,
	fromPromise,
	setup,
	waitFor,
} from "xstate";
// Workaround so that Typescript will produce .d.ts files for
// makeStateSyncClientMachine, whose return type depends on
// types in this module
import "xstate/guards";

import { type StateTransaction } from "../api-canvas-metadata/StateTransaction";
import { type TransactionProcessedConflict } from "../api-canvas-metadata/TransactionProcessedEvent";
import { type ActiveUser } from "../api-types";
import { UnauthorizedError } from "../http";
import { type ReceiveServerReplicaUpdate } from "./ReceiveServerReplicaUpdateEvent";
import { type StateSyncClientContext } from "./StateSyncClientContext";
import {
	type ConnectEvent,
	type EnqueueTransaction,
	type StateSyncClientEvent,
} from "./StateSyncClientEvent";

export interface LastTransactionProcessed {
	type: string;
	transaction: StateTransaction;
	error?: string;
	message?: string;
}

export type SubmitTransactionInput<TData extends Objectish> =
	| StateSyncClientContext<TData>["transactionQueue"][number]
	| undefined;

export type SubmitTransactionOutput = {
	lastAcknowledgedVersion: number;
	lastTransactionProcessed: LastTransactionProcessed;
};

export type ResolveTransactionConflictInput<TData extends Objectish> =
	StateSyncClientContext<TData>;

export type ResolveTransactionConflictOutput<TData extends Objectish> = Pick<
	StateSyncClientContext<TData>,
	"serverDataReplica"
> | null;

export interface StateSyncClientMachineArgs<TData extends Objectish> {
	connect: PromiseActorLogic<{ version: number; data: TData }, ConnectEvent>;
	submitTransaction: PromiseActorLogic<SubmitTransactionOutput, SubmitTransactionInput<TData>>;
	fetchMissingTransactions: PromiseActorLogic<unknown, { version: number }>;
}

export type StateSyncClientMachine<TData extends Objectish> = ReturnType<
	typeof makeStateSyncClientMachine<TData>
>;

export interface SetLastKnownServerState<TData extends Objectish> {
	type: "setLastKnownServerState";
	serverDataReplica: { data: TData; version: number };
}

export const ERROR_NO_ACTIVE_TRANSACTION = new Error("no active transaction");

function onSubmitTransactionUnknownError<TData extends Objectish>(
	context: StateSyncClientContext<TData>,
	params: { error: unknown }
) {
	let message: string;
	if (typeof params.error === "string") {
		message = params.error;
	} else if (params.error instanceof Error) {
		message = params.error.message;
	} else {
		message = `unknown error: ${String(params.error)}`;
	}

	return {
		// Just set last transaction processed to be an error.
		lastTransactionProcessed: {
			type: "error",
			error: "unknown",
			message,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			transaction: context.transactionQueue[0]!,
		} as const,
	};
}

export function makeStateSyncClientMachine<TData extends Objectish>({
	connect,
	submitTransaction,
	fetchMissingTransactions,
}: StateSyncClientMachineArgs<TData>) {
	return setup({
		actions: {
			//
			// Connection.
			//

			onConnectSuccess: assign((_, params: StateSyncClientContext<TData>["localData"]) => {
				return {
					localData: params,
					serverDataReplica: params,
					connectionError: undefined,
				};
			}),

			onConnectFailure: assign((_, params: { error: unknown }) => ({
				connectionError: params.error,
			})),

			//
			// Local cache management, e.g., optimistically updates.
			//

			optimisticallyUpdateLocalCache: assign(({ context }, params: EnqueueTransaction) => {
				if (params.options?.avoidOptimisticUpdate) {
					return context;
				}
				const newData = applyPatches(context.localData.data, params.patches);
				return {
					localData: {
						data: newData,
						version: context.localData.version + params.patches.length,
					},
				};
			}),

			//
			// Server replica management, e.g., bringing the local replica up-to-date by applying
			// patches from the server to it.
			//

			addServerReplicaUpdateToOpenUpdates: assign(
				({ context }, params: ReceiveServerReplicaUpdate) => {
					return {
						openServerDataReplicaUpdates: context.openServerDataReplicaUpdates.set(
							params.replicaVersion,
							params
						),
					};
				}
			),

			applyServerReplicaUpdates: assign(({ context }, params: { patches: Patch[] }) => {
				const newData = applyPatches(context.serverDataReplica.data, params.patches);
				const resolvedState = {
					data: newData,
					version: context.serverDataReplica.version + params.patches.length,
				};
				if (context.transactionQueue.length === 0) {
					// For server updates without any local transactions queued,
					// update the local data to be the resolved state sent from the server.
					return {
						localData: resolvedState,
						serverDataReplica: resolvedState,
					};
				}
				// Resolve the server replica and allow progression to the state machine.
				return {
					serverDataReplica: resolvedState,
				};
			}),

			//
			// Transaction submit success and failure cases.
			//

			queueTransactionForSubmission: assign(({ context }, params: EnqueueTransaction) => ({
				transactionQueue: [
					...context.transactionQueue,
					{
						id: params.transactionId ?? uuidV4(),
						lastVersionSeen: context.serverDataReplica.version,
						patches: params.patches,
						extras: params.options?.extras,
					},
				],
			})),

			onSubmitTransactionDone: assign(({ context }, params: SubmitTransactionOutput) => {
				const { lastAcknowledgedVersion, lastTransactionProcessed } = params;
				const { localData } = context;
				const patches = lastTransactionProcessed.transaction.patches;
				let resolvedState = context.serverDataReplica;
				if (
					context.serverDataReplica.version ===
					lastTransactionProcessed.transaction.lastVersionSeen
				) {
					resolvedState = {
						data: applyPatches(context.serverDataReplica.data, patches),
						version: context.serverDataReplica.version + patches.length,
					};
				}
				if (lastTransactionProcessed?.type === "success") {
					return {
						// Update local copy of the data.
						localData: { ...localData, version: lastAcknowledgedVersion },

						// Change the version on all the subsequent transactions, so they don't get
						// rejected.
						transactionQueue: context.transactionQueue.map((transaction) => ({
							...transaction,
							lastVersionSeen: lastAcknowledgedVersion,
						})),

						// Advance the server data replica if necessary.
						serverDataReplica:
							context.serverDataReplica.version === lastAcknowledgedVersion
								? { data: localData.data, version: lastAcknowledgedVersion }
								: resolvedState,

						// Set last transaction processed.
						lastTransactionProcessed,
					};
				} else if (lastTransactionProcessed.type === "replacement") {
					return {
						// Update local copy of the data.
						localData: resolvedState,

						// Change the version on all the subsequent transactions, so they don't get
						// rejected.
						transactionQueue: context.transactionQueue.map((transaction) => ({
							...transaction,
							lastVersionSeen: lastAcknowledgedVersion,
						})),

						// Advance the server data replica if necessary.
						serverDataReplica: resolvedState,

						// Set last transaction processed.
						lastTransactionProcessed: Object.assign({}, lastTransactionProcessed, {
							type: "success" as const,
						}),
					};
				} else if (lastTransactionProcessed?.error === "conflict") {
					return {
						transactionQueue: [],
						localData: context.serverDataReplica,
						transactionConflictQueue: context.transactionQueue.map((transaction) => {
							return {
								type: "error",
								error: "conflict",
								message: "transaction abandoned due to conflict",
								transaction,
							} as const;
						}),
					};
				} else if (lastTransactionProcessed?.error === "validation-failure") {
					return {
						// Just set last transaction processed, this transaction was rejected.
						// This can be reported back to the user if helpful.
						lastTransactionProcessed,
						localData: context.serverDataReplica,
					};
				} else if (
					lastTransactionProcessed?.error === "unknown" ||
					lastTransactionProcessed?.error === "persistence-failing"
				) {
					return {
						...onSubmitTransactionUnknownError(context, {
							error: lastTransactionProcessed.message,
						}),
						localData: context.serverDataReplica,
					};
				}

				return onSubmitTransactionUnknownError(context, {
					error: `unknown error: no transaction processed`,
				});
			}),

			// This gets called when the `submitTransaction` actor crashes unexpectedly.
			onSubmitTransactionUnknownFailure: assign(({ context }, params: { error: unknown }) => {
				return {
					...onSubmitTransactionUnknownError(context, params),
					localData: context.serverDataReplica,
				};
			}),

			//
			// Reconcile conflict.
			//

			discardConflict: assign(({ context }) => {
				return {
					lastTransactionProcessed:
						context.transactionConflictQueue[0] ?? context.lastTransactionProcessed,
				};
			}),

			//
			// Transaction queue.
			//

			popTransactionQueue: assign({
				transactionQueue: ({ context }) => context.transactionQueue.slice(1),
			}),

			//
			// Conflict queue.
			//

			popConflictQueue: assign({
				transactionConflictQueue: ({ context }) =>
					context.transactionConflictQueue.slice(1),
			}),

			//
			// Other.
			//

			resetLocalStateToLastGood: assign({
				localData: ({ context: { serverDataReplica } }) => ({
					data: serverDataReplica.data,
					version: serverDataReplica.version,
				}),
			}),

			onActiveUsersUpdated: assign((_, params: { activeUsers: ActiveUser[] }) => ({
				activeUsers: params.activeUsers,
			})),
		},
		actors: {
			connect,
			submitTransaction,
			fetchMissingTransactions,
			resolveConflict: fromPromise<
				ResolveTransactionConflictOutput<TData>,
				ResolveTransactionConflictInput<TData>
			>(async ({ input: context }) => {
				//
				// We need to fetch the
				//

				// const [activeConflict, ...rest] = context.transactionConflictQueue;

				return { serverDataReplica: context.serverDataReplica };
			}),
		},
		guards: {
			// Checks whether `serverReplicaData` has fallen behind the server's current version,
			// and requires us to fetch missing versions. For example, if our local copy is version
			// 9 and the server sends us an update expecting version 12, we are missing versions 10
			// and 11, and we'll need to fetch them.
			serverReplicaDataMissingUpdates: ({ context }) => {
				const lastSeenVersion = context.serverDataReplica.version;
				// There's a more efficient way to do this, but we expect this to be at worst a few
				// transactions, so it's fine for now.
				const [earlierVersions] = context.openServerDataReplicaUpdates.partition(
					(_, version) => version <= lastSeenVersion
				);

				const replicaInSync = earlierVersions.size === 1;
				return replicaInSync;
			},

			// Checks whether an update receieved from the server can be applied to the replica. The
			// replica's version must match the server version *EXACTLY*, or the patch can't be
			// applied, as we'd be behind.
			serverReplicaUpdateCanBeApplied: ({ context }, params: ReceiveServerReplicaUpdate) =>
				context.serverDataReplica.version === params.replicaVersion,
		},
		delays: {},
		types: {
			context: {} as StateSyncClientContext<TData>,
			events: {} as
				| StateSyncClientEvent
				| { type: "connectionFailureDetected" }
				| { type: "connectionRestored" }
				| ReceiveServerReplicaUpdate
				| { type: "enqueueAsyncServerReplicaUpdate" }
				| SetLastKnownServerState<TData>
				| { type: "persistenceError" }
				| { type: "activeUsersUpdated"; activeUsers: ActiveUser[] },
		},
	}).createMachine({
		/** @xstate-layout N4IgpgJg5mDOIC5SwC4EMVgLSwJ4DsBjLQgGwEsx8UA6ciUsAYkIHt98xCUBtABgC6iUAAdWscinLthIAB6IAjACYAHIpoBmZYoCcAdl19FAFk27NmgDQhciTfs01V+gGzKDugKyaTq3bomAL5BNqgY2HhEJBRUtGwcXFL4UEwQ7GB0+ABurADWmQBmYCiEABYAwmg5aLAAsiVoEBho-EJIIGISUjIdCggqNnYDyiFh6Jg4BMRklNQ0CZzc5ClMYABO66zrNCKkGIXbALY0xaWV1dm1DejN6G2yXZLS+LL9Jq76NAEOXsquf0CHiGSj4mj4NB0fj4AR8yi8ujGIHCkyiM1i80WSRWqQg5FgWO4Dw6Tx6rz6SjUGm0ekMxjMFmstnsyichi8fC8+n0Lj4+kUXiRKMi0xic3i7CWmAgaXxhN4gke4mevVA-UCbPhfJ8lkCumUIIQmi8qhoJnNfE5ikUqipqiFExF0VmcQWkqSkDW+AAjgBXMD+gAq62qsDQy3YxNEyrJb0QDJo+g5YNcAU5PP0JkNHl0ND4-2U+nzrJMXi8rmCoWRjqmzoxEsS3E9sBKwdD4bJAAUtoQ4BIUp20L6WxAo50Yy840b9TQEQ5VOXNAL-mZs2Czd5jH8F65d-aq8La+jxW7G9KmGiylt8OQAF5gADKG2yGwfjrHpMnFIQJn+ZphJjGP4ijavohrqDQ1oOPyfDmuYyj5g6ERHmKrryp68ovAAYmg5CMKOiokhOqryIgGqJlqSaWOYJj6oarimN8XI+OoehGAuSGoqKLqYu6TYQDQADuuHJFAWHbG2+BhhGUlMB+xHkmqiAmhCyhUoEML+JYYHMj+3izsY8IfIoDF8Ko+7jMhaKobxZ6QDQsC+gARkckiiZJ0lkmkGRZLkBQOc5rkoB5HYvPJ3RfkpP5-Imlp8K48XeO4+j-PRxqQYo86FgxaaIgeNbWTxDZSvZjkuW5OIhTJaybNsuz7CghzrCcZVBVVZLhSqimkdFyixZaCWpuWhapbpXi-rOtpeFBYKaBWricU6x5oXxZIAKK1esLCrZ1sbfh8Xw-Em-yAr+uiGroqgQplxqpjyriqIBlZVvgrAQHAsiHoV9ZKhFJH9Fgc2GlgSZ5hY3IgXBuh6At+VWdx9Z0AwYC-V1U4gS4kICjBFggQ4Wa6ZYrg0A93IfENqi7pWllcXWJ6YSkqN7VFnwXeWkGbjRtHKB8eU00tNnFR6EBM5FPXArpgGmoBSamB8JiOLBi0oUVp4lQJwnPCk4nrO1LzwERf3df0D0mM4j3mtNrJuMYhocsTJg2gyD3LvoyvffTq2lYFFUpHrJGfv9iDcnbmimuCZiU9pCGCnDtPLbZUovBtWzrKLQcIKzumZayiY7tBCFFm7IRBEAA */
		id: "state-sync-client",
		initial: "idle",
		context: {
			// FIXME: This is not at all correct lol.
			localData: {
				data: {} as TData,
				version: 0,
			},
			serverDataReplica: {
				data: {} as TData,
				version: 0,
			},

			editingEnabled: true,
			transactionQueue: [] as StateTransaction[],
			lastTransactionProcessed: null,

			transactionConflictQueue: [] as TransactionProcessedConflict[],

			openServerDataReplicaUpdates: im.OrderedMap<number, ReceiveServerReplicaUpdate>(),

			activeUsers: [] as ActiveUser[],
		},
		on: {
			activeUsersUpdated: {
				actions: [
					{
						type: "onActiveUsersUpdated",
						params: ({ event }) => {
							return { activeUsers: event.activeUsers };
						},
					},
				],
			},
		},
		states: {
			idle: {
				description: "Waiting for instructions.",
				on: { connect: { target: "connecting" }, persistenceError: { target: "terminal" } },
			},
			connecting: {
				description: "Connecting to state sync server.",
				invoke: {
					src: "connect",
					id: "connect",
					input: ({ event }) => {
						if (event.type !== "connect") {
							throw new Error(
								`Metadata server requires initialization to occur via an 'connect' event, got '${event.type}'`
							);
						}
						return event;
					},
					onDone: [
						{
							target: "connected",
							actions: [
								{
									type: "onConnectSuccess",
									params: ({ event }) => {
										return {
											data: event.output.data,
											version: event.output.version,
										};
									},
								},
							],
						},
					],
					onError: [
						{
							target: "connectionFailed",
							actions: { type: "onConnectFailure", params: ({ event }) => event },
						},
					],
				},
				on: { disconnect: { target: "idle" }, persistenceError: { target: "terminal" } },
			},
			connected: {
				type: "parallel",
				states: {
					editableStatus: {
						initial: "enabled",
						states: {
							enabled: {
								always: {
									target: "disabled",
									guard: ({ context }) => !context.editingEnabled,
								},
								on: {
									enqueueTransaction: {
										actions: [
											{
												type: "queueTransactionForSubmission",
												params: ({ event }) => event,
											},
											{
												type: "optimisticallyUpdateLocalCache",
												params: ({ event }) => event,
											},
										],
									},
								},
							},
							disabled: {
								always: {
									target: "enabled",
									guard: ({ context }) => context.editingEnabled,
								},
							},
						},
					},
					processingLocalEdits: {
						description: "State client is accepting edits to the local cache.",
						initial: "waitingForTransactions",
						states: {
							waitingForTransactions: {
								always: {
									target: "submittingTransaction",
									guard: ({ context }) =>
										context.editingEnabled &&
										context.transactionQueue.length > 0,
								},
							},
							submittingTransaction: {
								invoke: {
									src: "submitTransaction",
									id: "submitTransaction",
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									input: ({ context }) => context.transactionQueue[0]!,
									onDone: [
										{
											target: "waitingForTransactions",
											description:
												"Called on both transaction accept error, e.g., conflict",
											actions: [
												{
													type: "onSubmitTransactionDone",
													params: ({ event }) => event.output,
												},
												{ type: "popTransactionQueue" },
											],
										},
									],
									onError: [
										{
											description:
												"Called ONLY when transaction returns 401 (Unauthorized).",
											guard: ({ event }) =>
												event.error instanceof UnauthorizedError,
											target: "#state-sync-client.terminal",
											actions: [
												{
													type: "onSubmitTransactionUnknownFailure",
													params: ({ event }) => event,
												},
												{ type: "popTransactionQueue" },
											],
										},
										{
											target: "waitingForTransactions",
											description:
												"Called ONLY when the actor crashes unexpectedly.",
											actions: [
												{
													type: "onSubmitTransactionUnknownFailure",
													params: ({ event }) => event,
												},
												{ type: "popTransactionQueue" },
											],
										},
									],
								},
							},
							processingPaused: {},
						},
					},
					synchronizingServerReplica: {
						description:
							"State client is connected to the server and\n\nsynchronizing the local replica of its data.",
						initial: "waitingForUpdates",
						states: {
							waitingForUpdates: {
								always: {
									target: "fetchingMissingUpdates",
									guard: "serverReplicaDataMissingUpdates",
								},
							},
							fetchingMissingUpdates: {
								invoke: {
									src: "fetchMissingTransactions",
									id: "fetchingMissingUpdates",
									input: ({ context }) => ({
										version: context.serverDataReplica.version,
									}),
									onDone: [{ target: "waitingForUpdates" }],
									onError: [{ target: "cannotRetrieveMissingUpdates" }],
								},
							},
							cannotRetrieveMissingUpdates: {},
						},
						on: {
							receiveServerReplicaUpdate: [
								{
									target: "synchronizingServerReplica",
									guard: {
										type: "serverReplicaUpdateCanBeApplied",
										params: ({ event }) => event,
									},
									actions: {
										type: "applyServerReplicaUpdates",
										params: ({ event }) => event,
									},
									description:
										"Called when the server pushes an update to this client.",
								},
								{
									target: "synchronizingServerReplica",
									actions: {
										type: "addServerReplicaUpdateToOpenUpdates",
										params: ({ event }) => event,
									},
								},
							],
						},
					},

					reconcilingLocalWithServerState: {
						description:
							"Reconciling the locally-editable cache with the replica of the server state.",
						initial: "waitingForTransactions",
						states: {
							waitingForTransactions: {
								always: {
									guard: ({ context }) => {
										return context.transactionConflictQueue.length > 0;
									},
									actions: [
										// "Reconcile" conflict.
										{ type: "discardConflict" },
										{ type: "popConflictQueue" },
									],
								},
							},
						},
					},
				},
				on: {
					disconnect: {
						target: "idle",
					},

					setLastKnownServerState: {
						target: "connected",
						actions: [
							assign({ serverDataReplica: ({ event }) => event.serverDataReplica }),
							assign({
								localData: ({
									context: {
										editingEnabled: transactionProcessingPaused,
										localData,
									},
									event: { serverDataReplica },
								}) => {
									if (
										transactionProcessingPaused ||
										serverDataReplica.version <= localData.version
									) {
										return localData;
									}
									return {
										data: serverDataReplica.data,
										version: serverDataReplica.version,
									};
								},
							}),
						],
					},
					connectionFailureDetected: {
						target: "connectionFailed",
						actions: { type: "resetLocalStateToLastGood" },
					},
					persistenceError: {
						target: "terminal",
					},
				},
			},
			connectionFailed: {
				on: {
					connect: { target: "connecting" },
					connectionRestored: { target: "connected" },
				},
			},
			terminal: {
				description: "The state machine has reached a terminal state.",
			},
		},
	});
}

export function waitForTransactionProcessed<TData extends Objectish>(
	sys: ReturnType<typeof createActor<StateSyncClientMachine<TData>>>,
	transactionId: string
): ReturnType<typeof waitFor<ReturnType<typeof createActor<StateSyncClientMachine<TData>>>>> {
	return waitFor(
		sys,
		// IMPORTANT: We do NOT wait for the state to be, e.g., `waitingForTransactions`. This is
		// because `waitFor` does not capture "micro-transactions" like those from `always` states.
		// And in any event, this is enough to capture the first instance that the transaction has
		// been fully processed.
		(snap) => snap.context.lastTransactionProcessed?.transaction.id === transactionId
	);
}
